import React, { useEffect, useMemo, useRef, useState } from 'react';

import cx from 'classnames';
import { MOUSE_DOWN_EVENT } from 'constants/events';
import { useAssignmentTemplates } from 'sdk';

import { ReactComponent as CheckIcon } from 'pages/Teachers/Assignments/CreateMath/assets/check-icon.svg';
import { ReactComponent as FilterIcon } from 'pages/Teachers/Assignments/CreateMath/assets/filter-icon.svg';
import { ReactComponent as SearchIcon } from 'pages/Teachers/Assignments/CreateMath/assets/search-icon.svg';
import TemplateFilters from 'pages/Teachers/Assignments/CreateMath/components/TemplateFilters';
import {
  GRADE_OPTIONS,
  getInitialAcceleratedState
} from 'utils/curricullums/mapper';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';

import styles from './styles.module.scss';

const TemplateDropdown = ({
  selectedTemplate,
  onSelectTemplate,
  trackEvent
}) => {
  const [templateSearchText, setTemplateSearchText] = useState(
    selectedTemplate?.assignment_template_title || ''
  );
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [accumulatedTemplates, setAccumulatedTemplates] = useState([]);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [templateFilters, setTemplateFilters] = useState({
    is_accelerated: getInitialAcceleratedState(),
    root: '',
    grade: '',
    unit: '',
    lesson: '',
    search: '',
    limit: 20,
    offset: 0
  });

  const apiFilters = useMemo(() => {
    const selectedGradeObj = GRADE_OPTIONS.find(
      (opt) => opt.key === templateFilters.grade
    );

    const mappedGrade =
      selectedGradeObj !== undefined ? selectedGradeObj.value : '';

    return {
      is_accelerated: templateFilters.is_accelerated,
      root: templateFilters.root,
      grade: mappedGrade,
      unit: templateFilters.unit,
      lesson: templateFilters.lesson,
      search: templateFilters.search,
      limit: templateFilters.limit,
      offset: templateFilters.offset
    };
  }, [templateFilters]);

  const { data: rawTemplatesResponse, isFetching } =
    useAssignmentTemplates(apiFilters) || {};

  // Debounce user typing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setTemplateFilters((prev) => {
        if (prev.search === templateSearchText) return prev;
        return { ...prev, search: templateSearchText, offset: 0 };
      });
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [templateSearchText]);

  // Reset accumulated templates when core filters change
  useEffect(() => {
    setAccumulatedTemplates([]);
    setTemplateFilters((prev) => ({ ...prev, offset: 0 }));
    setHasMore(true);
  }, [
    templateFilters.root,
    templateFilters.grade,
    templateFilters.unit,
    templateFilters.lesson,
    templateFilters.is_accelerated,
    templateFilters.search
  ]);

  // Accumulate paginated data
  useEffect(() => {
    const fetchedTemplates = Array.isArray(rawTemplatesResponse)
      ? rawTemplatesResponse
      : rawTemplatesResponse?.results || [];

    if (fetchedTemplates.length > 0) {
      setAccumulatedTemplates((prev) => {
        const combined = [...prev, ...fetchedTemplates];
        return Array.from(
          new Map(combined.map((t) => [t.assignment_template_id, t])).values()
        );
      });
    }

    if (rawTemplatesResponse && fetchedTemplates.length < 20) {
      setHasMore(false);
    }
  }, [rawTemplatesResponse]);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowTemplateDropdown(false);
        setShowFilters(false);
      }
    };
    document.addEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
    return () =>
      document.removeEventListener(MOUSE_DOWN_EVENT, handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    setTemplateSearchText(e.target.value);
    setShowTemplateDropdown(true);
    setShowFilters(false);
    if (selectedTemplate) {
      onSelectTemplate(null);
    }
  };

  const handleSelect = ({ template }) => {
    setTemplateSearchText(template.assignment_template_title);
    setShowTemplateDropdown(false);
    onSelectTemplate(template);

    trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
      feature: 'create',
      view: 'template',
      form: 'submit',
      template: template.assignment_template_title
    });
  };

  const handleTemplateScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 50;

    if (isNearBottom && !isFetching && hasMore) {
      setTemplateFilters((prev) => ({ ...prev, offset: prev.offset + 20 }));
    }
  };

  const handleFilterReset = () => {
    setTemplateFilters((prev) => ({
      ...prev,
      root: '',
      grade: '',
      unit: '',
      lesson: '',
      search: ''
    }));
  };

  return (
    <div className={styles.searchWrapper} ref={containerRef}>
      <FilterIcon
        className={cx(styles.filterIcon, { [styles.active]: showFilters })}
        onClick={(e) => {
          e.stopPropagation();
          const next = !showFilters;
          setShowFilters(next);
          if (next) setShowTemplateDropdown(false);
        }}
      />
      <input
        ref={inputRef}
        type="text"
        placeholder="Search or Select a Template..."
        value={templateSearchText}
        onChange={handleSearchChange}
        onClick={() => {
          setShowTemplateDropdown(true);
          setShowFilters(false);
        }}
        className={styles.templateInput}
      />
      <SearchIcon
        className={styles.searchIcon}
        onClick={() => {
          inputRef.current.focus();
        }}
      />

      {showTemplateDropdown && (
        <div
          className={styles.templateFloatingList}
          onScroll={handleTemplateScroll}
        >
          {accumulatedTemplates.length === 0 && !isFetching ? (
            <div className={styles.emptyTemplatesMessage}>
              No templates found for the selected filters.
            </div>
          ) : (
            accumulatedTemplates.map((template) => {
              const isSelected =
                selectedTemplate?.assignment_template_id ===
                template.assignment_template_id;
              return (
                <div
                  key={template.assignment_template_id}
                  className={cx(styles.templateItem, {
                    [styles.selected]: isSelected
                  })}
                  onClick={() => handleSelect({ template: template })}
                >
                  <div className={styles.checkWrapper}>
                    {isSelected && <CheckIcon className={styles.checkIcon} />}
                  </div>
                  <div className={styles.itemContent}>
                    <div className={styles.templateItemName}>
                      {template.assignment_template_title}
                    </div>
                    <div className={styles.templateItemMeta}>
                      <span className={styles.tag}>{template.root_name}</span>
                      <span className={styles.tag}>{template.grade_tag}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {isFetching && (
            <div className={styles.loadingMessage}>Loading...</div>
          )}
        </div>
      )}

      {showFilters && (
        <div
          className={styles.filterFloatingPanel}
          onClick={(e) => e.stopPropagation()}
        >
          <TemplateFilters
            filters={templateFilters}
            setFilters={setTemplateFilters}
            onClearSearch={() => setTemplateSearchText('')}
            onFiltersReset={() => handleFilterReset()}
          />
        </div>
      )}
    </div>
  );
};

export default TemplateDropdown;
